/**
 * Os ícones desenhados do app.
 *
 * ## Desenhados aqui, e não de uma biblioteca
 *
 * Dez ícones não justificam uma dependência — e uma biblioteca traria centenas
 * de glifos para o bundle de um app que usa sete. Traço de 1,75px em
 * `currentColor`, todos no mesmo `viewBox` de 24: assim eles trocam de cor com
 * o estado de quem os hospeda sem nenhuma regra a mais, e pesam o mesmo lado a
 * lado.
 *
 * ## Um arquivo só, e não um por lugar
 *
 * Este era `tab-icons.tsx`, das quatro abas. Os três atalhos do Dashboard
 * nasceriam num segundo arquivo com um segundo `COMUM` — e dois traços de
 * espessura diferente lado a lado é exatamente o que uma constante
 * compartilhada evita. O que os une não é onde aparecem, é como são feitos.
 *
 * ## Por que cada um é o que é
 *
 * - **Casa** para o Dashboard: é o resumo da casa, e é a tela inicial.
 * - **Duas setas** para Transações: dinheiro que entra e dinheiro que sai é
 *   literalmente o que a tela lista. Uma seta só diria metade.
 * - **Etiqueta** para Produtos: o catálogo existe para responder quanto custa
 *   por litro — a etiqueta de preço é o objeto daquela pergunta.
 * - **Lista com marcas** para Lembretes: é uma lista de compras, e a marca é o
 *   gesto que a pessoa faz nela.
 * - **Cupom com o rasgo** para Notas: é a forma da nota fiscal de papel, e o
 *   serrilhado de baixo é o que a distingue de "documento" em geral.
 * - **Barras** para Relatórios: a tela é comparação entre meses, e barras são o
 *   desenho dessa comparação.
 * - **Sino** para Avisos: é a convenção que não precisa ser aprendida.
 * - **Mais** para lançar transação: é o `+` do FAB, e ele gira 45° quando o
 *   formulário está aberto — vira um ✕ sem trocar de desenho, porque é o mesmo
 *   botão que fecha.
 * - **Bandeja de entrada** para Classificar: a tela é uma fila de triagem —
 *   o monte de "sem categoria" esperando para ir cada um para o seu lugar. As
 *   quatro candidatas mais óbvias foram desenhadas e recusadas ao olho, todas
 *   por colisão: a **bifurcação** é o *shuffle* de player de música, a
 *   **bandeja com seta para dentro** é o ícone universal de download, a
 *   **etiqueta** já é a de Produtos (e a barra de abas está na mesma tela), e a
 *   **rosca** briga com as barras de Relatórios — duas telas viram gráfico. A
 *   bandeja vazia não colide com nenhum dos dez.
 * - **Cartão** para Cadastros: contas e cartões são o que a tela guarda, e o
 *   retângulo com a tarja é o objeto mais reconhecível dos dois.
 * - **Banco** para a transação que saiu da CONTA, ao lado do cartão na lista de
 *   transações. O par é o ponto: sozinho, nenhum dos dois diria muita coisa;
 *   lado a lado na mesma coluna, a diferença entre um retângulo com tarja e um
 *   frontão com colunas se lê sem legenda.
 * - **Lápis** para editar e **lixeira** para apagar, nos controles de linha da
 *   discussão. São as duas convenções mais firmes da interface — não há um
 *   segundo desenho concorrendo por nenhum dos dois sentidos, e é por isso que
 *   estes dois podem perder a palavra ao lado sem perder o significado.
 * - **Reticências** para "mais ações", na linha da nota: o que não coube à vista
 *   mora atrás delas, e é a convenção do sistema para isso.
 * - **Setas opostas na horizontal** para trocar o produto de uma linha. As
 *   duas setas na VERTICAL já são as de Transações, e a barra de abas está
 *   na mesma tela; deitadas, elas dizem "troca uma coisa pela outra".
 * - **Seta que salta** para pular: passa por cima do ponto sem tocá-lo, que é
 *   o que "pular" faz — a linha continua na conta, só não vira produto.
 * - **Controles deslizantes** para Ajustes: a tela é onde se regula o app, e
 *   três trilhos com o botão em alturas diferentes dizem "regular" sem
 *   palavra. A engrenagem foi considerada e recusada pelo tamanho: em 24px
 *   com traço de 1,75 os dentes viram um sol, e o sol não quer dizer nada
 *   aqui. Os botões interrompem a linha em vez de ficar por cima dela — com
 *   `fill: none`, uma linha atravessando o círculo leria como um nó.
 * - **Visto duplo** para "marcar todas como lidas", nos avisos. O visto simples
 *   já é "aceitar" na linha da nota, e aqui o gesto é outro: não é aceitar uma
 *   coisa, é dar a fila inteira por vista. O segundo visto é a convenção de
 *   "lido" que os mensageiros ensinaram, e ela chega pronta.
 */

const COMUM = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

export function IconeCasa() {
  return (
    <svg {...COMUM}>
      <path d="M3 10.6 12 3.5l9 7.1V20a1 1 0 0 1-1 1h-4.5v-6h-7v6H4a1 1 0 0 1-1-1z" />
    </svg>
  )
}

export function IconeFluxo() {
  return (
    <svg {...COMUM}>
      <path d="M8 20V5m0 0L4.5 8.5M8 5l3.5 3.5" />
      <path d="M16 4v15m0 0 3.5-3.5M16 19l-3.5-3.5" />
    </svg>
  )
}

export function IconeEtiqueta() {
  return (
    <svg {...COMUM}>
      <path d="M3.5 12.6V5.5a2 2 0 0 1 2-2h7.1a2 2 0 0 1 1.4.6l6.9 6.9a2 2 0 0 1 0 2.8l-7.1 7.1a2 2 0 0 1-2.8 0L4.1 14a2 2 0 0 1-.6-1.4z" />
      <circle cx="8.2" cy="8.2" r="1.4" />
    </svg>
  )
}

export function IconeLista() {
  return (
    <svg {...COMUM}>
      <path d="m3.5 6.5 1.4 1.4L7.6 5.2" />
      <path d="m3.5 12.5 1.4 1.4 2.7-2.7" />
      <circle cx="5" cy="18.5" r="1.6" />
      <path d="M11 6.6h9.5M11 12.6h9.5M11 18.6h9.5" />
    </svg>
  )
}

export function IconeNota() {
  return (
    <svg {...COMUM}>
      {/* O serrilhado de baixo é o rasgo do cupom — sem ele isto é o ícone de
          "documento", que já quer dizer outra coisa em qualquer app. */}
      <path d="M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z" />
      <path d="M9 8h6M9 12h6" />
    </svg>
  )
}

export function IconeRelatorio() {
  return (
    <svg {...COMUM}>
      <path d="M4 20h16" />
      {/* Alturas diferentes de propósito: barras iguais desenham uma cerca, e o
          que a tela mostra é justamente a diferença entre os meses. */}
      <path d="M7 20v-6M12 20V5M17 20v-9" />
    </svg>
  )
}

export function IconeSino() {
  return (
    <svg {...COMUM}>
      <path d="M18 8.5a6 6 0 0 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5" />
      <path d="M10.5 19a2 2 0 0 0 3 0" />
    </svg>
  )
}

export function IconeMais() {
  return (
    <svg {...COMUM}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

export function IconeTriagem() {
  return (
    <svg {...COMUM}>
      {/* A boca da caixa desenhada por dentro: é ela que faz isto ser uma
          bandeja de entrada e não uma caixa fechada, e é o que se enxerga
          primeiro a 24px. */}
      <path d="M21 12h-5l-1.5 2.5h-5L8 12H3" />
      <path d="M6.2 4.9 3 12v5.5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V12l-3.2-7.1A2 2 0 0 0 16 3.7H8a2 2 0 0 0-1.8 1.2z" />
    </svg>
  )
}

export function IconeCartao() {
  return (
    <svg {...COMUM}>
      <rect x="2.75" y="5" width="18.5" height="14" rx="2.5" />
      <path d="M2.75 9.75h18.5" />
      <path d="M6.5 14.75h4" />
    </svg>
  )
}

export function IconeBanco() {
  return (
    <svg {...COMUM}>
      {/* Frontão e colunas: é o desenho de "banco" que não depende de marca
          nenhuma, e a única forma na família que não é um retângulo. */}
      <path d="M3 9.5 12 4l9 5.5" />
      <path d="M5.5 10v7M10 10v7M14 10v7M18.5 10v7" />
      <path d="M3 20h18" />
    </svg>
  )
}

export function IconeDividir() {
  return (
    <svg {...COMUM}>
      {/* Uma barra partida ao meio, e as duas metades separadas por uma folga:
          é a conta que o botão faz — o resto vira N pedaços iguais. Um "÷" seria
          o símbolo da operação; isto é o resultado dela. */}
      <rect x="2.75" y="6" width="8" height="12" rx="1.75" />
      <rect x="13.25" y="6" width="8" height="12" rx="1.75" />
    </svg>
  )
}

export function IconeCheque() {
  return (
    <svg {...COMUM}>
      <path d="m4.5 12.5 5 5 10-11" />
    </svg>
  )
}

export function IconeX() {
  return (
    <svg {...COMUM}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  )
}

export function IconeLapis() {
  return (
    <svg {...COMUM}>
      {/* A ponta encostando no canto de baixo, e não um lápis flutuando no
          meio: é o gesto de escrever que se lê, não o objeto. */}
      <path d="M4 20h4L19 9a2.83 2.83 0 0 0-4-4L4 16v4Z" />
      {/* A virola. Sem ela o corpo vira uma seta diagonal qualquer. */}
      <path d="m14 6 4 4" />
    </svg>
  )
}

export function IconeLixeira() {
  return (
    <svg {...COMUM}>
      <path d="M4 6.5h16" />
      <path d="M9 6.5v-2a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4.5v2" />
      <path d="M18 6.5v13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-13" />
      {/* As duas ranhuras são o que separa uma lixeira de um balde. */}
      <path d="M10.5 11v6M13.5 11v6" />
    </svg>
  )
}

export function IconeReticencias() {
  return (
    <svg {...COMUM}>
      {/* Pontos de traço redondo com comprimento quase zero: com `fill: none`
          um círculo seria um anel, e anel de 1px some ao lado do lápis. */}
      <path d="M5.5 12h.01M12 12h.01M18.5 12h.01" strokeWidth={3} />
    </svg>
  )
}

export function IconeTrocar() {
  return (
    <svg {...COMUM}>
      <path d="M4 8.5h15m0 0-3.5-3.5M19 8.5 15.5 12" />
      <path d="M20 15.5H5m0 0 3.5-3.5M5 15.5 8.5 19" />
    </svg>
  )
}

export function IconePular() {
  return (
    <svg {...COMUM}>
      <path d="M4 16c1.5-5 4.5-7.5 8-7.5s6.5 2.5 8 7.5" />
      <path d="m20 16-4-1m4 1 1-4" />
      {/* O ponto que ficou para trás, embaixo do arco. */}
      <path d="M12 17h.01" strokeWidth={3} />
    </svg>
  )
}

export function IconeChequeDuplo() {
  return (
    <svg {...COMUM}>
      <path d="m3 12.5 4.5 4.5 8-9" />
      {/* O segundo, deslocado: sozinho ele viraria um visto gordo. */}
      <path d="m11 15 1.5 2 8.5-9.5" />
    </svg>
  )
}

export function IconeAjustes() {
  return (
    <svg {...COMUM}>
      <path d="M4 6h3M11 6h9M4 12h9M17 12h3M4 18h1M9 18h11" />
      <circle cx="9" cy="6" r="2" />
      <circle cx="15" cy="12" r="2" />
      <circle cx="7" cy="18" r="2" />
    </svg>
  )
}
